import React, { useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuthStore } from '@/stores/authStore';
import { useThemeStore } from '@/stores/themeStore';

// ClientLayout — прежняя обёртка, оставлена в кодовой базе, но не
// импортируется, чтобы не тянуть его код в бандл. Полный откат — вернуть
// импорт и заменить <DesignAppShell /> в Route ниже.
import DesignAppShell from '@/design/AppShell';
import ProtectedRoute from '@/components/layout/ProtectedRoute';
// Design-transfer prototype (ветка supportcrm). Стили scope'нуты внутрь
// .dc-контейнера — существующие Tailwind-стили страниц не задевают.
import '@/design/styles.dc.css';
import '@/design/monochrome-lock.dc.css';

import HomePage from '@/pages/Home/HomePage';
import LoginPage from '@/pages/Login/LoginPage';
import DashboardPage from '@/pages/Dashboard/DashboardPage';
import TicketsListPage from '@/pages/Tickets/TicketsListPage';
// Design-transfer views (см. design/README): постепенно заменяют
// внутренности страниц. Данные — реальные API, не моки.
import { HomeView as DesignHomeView } from '@/design/views/HomeView';
import { TicketsListView as DesignTicketsListView } from '@/design/views/TicketsListView';
import { CreateTicketView as DesignCreateTicketView } from '@/design/views/CreateTicketView';
import { FaqView as DesignFaqView } from '@/design/views/FaqView';
import { PassportView as DesignPassportView } from '@/design/views/PassportView';
import { ProfileView as DesignProfileView } from '@/design/views/ProfileView';
import { ContactsView as DesignContactsView } from '@/design/views/ContactsView';
import CreateTicketPage from '@/pages/Tickets/CreateTicketPage';
import TicketDetailPage from '@/pages/Tickets/TicketDetailPage';
import KnowledgeBasePage from '@/pages/KnowledgeBase/KnowledgeBasePage';
import ArticlePage from '@/pages/KnowledgeBase/ArticlePage';
import KnowledgeArticleEditorPage from '@/pages/KnowledgeBase/KnowledgeArticleEditorPage';
import FaqPage from '@/pages/Faq/FaqPage';
import TrainingPage from '@/pages/Training/TrainingPage';
import MyDataPage from '@/pages/MyData/MyDataPage';
import PassportPage from '@/pages/Passport/PassportPage';
import ContactsPage from '@/pages/Contacts/ContactsPage';
import NotFoundPage from '@/pages/NotFoundPage';
import ComponentsGallery from '@/pages/internal/ComponentsGallery';

const App: React.FC = () => {
  const { checkAuth, isAuthenticated } = useAuthStore();
  const { initTheme } = useThemeStore();

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  useEffect(() => {
    initTheme();
  }, [initTheme]);

  return (
    <Routes>
      {/* Public routes */}
      <Route path="/" element={isAuthenticated ? <Navigate to="/dashboard" replace /> : <HomePage />} />
      <Route path="/login" element={isAuthenticated ? <Navigate to="/dashboard" replace /> : <LoginPage />} />
      {/* Регистрация на портале отключена — вход только по выданным данным.
          /register → на вход. */}
      <Route path="/register" element={<Navigate to="/login" replace />} />

      {/* Protected routes with layout. Дизайн-shell (design-transfer из
          supportcrm-ветки) — sidebar/topbar в стилях Club:Core Printstream.
          ClientLayout больше не используется для protected-роутов, но
          остаётся в кодовой базе (unused import — оставляем на всякий,
          если понадобится быстрый откат в MR). */}
      <Route
        element={
          <ProtectedRoute>
            <DesignAppShell />
          </ProtectedRoute>
        }
      >
        <Route path="/dashboard" element={<DesignHomeView />} />
        <Route path="/tickets" element={<DesignTicketsListView />} />
        <Route path="/tickets/new" element={<DesignCreateTicketView />} />
        <Route path="/tickets/:id" element={<TicketDetailPage />} />
        <Route path="/knowledge" element={<KnowledgeBasePage />} />
        <Route path="/knowledge/new" element={<KnowledgeArticleEditorPage />} />
        <Route path="/knowledge/:slug" element={<ArticlePage />} />
        <Route path="/faq" element={<DesignFaqView />} />
        <Route path="/training" element={<TrainingPage />} />
        <Route path="/my-data" element={<DesignProfileView />} />
        <Route path="/passport" element={<DesignPassportView />} />
        <Route path="/contacts" element={<DesignContactsView />} />
      </Route>

      {/* Internal dev-only gallery (DEV build or ?dev=1) */}
      {(import.meta.env.DEV || (typeof window !== 'undefined' && new URLSearchParams(window.location.search).has('dev'))) && (
        <Route path="/internal/components" element={<ComponentsGallery />} />
      )}

      {/* 404 */}
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
};

export default App;
